/*
 * Live Feedback panel — toggle, repetition config, editorial suggestion card,
 * and the read-only repetitions list. Polling lifecycle lives in
 * live/liveController.ts (module singleton, runs across tabs). This panel only
 * renders store data and forwards user intent to the controller.
 */

import {
  Badge,
  Button,
  Card,
  Field,
  MessageBar,
  MessageBarBody,
  SpinButton,
  Switch,
  Text,
  makeStyles,
  tokens,
  type SpinButtonOnChangeData,
} from "@fluentui/react-components";
import { type SyntheticEvent } from "react";
import { useStore } from "../../state/store";
import { hasLiveFindings } from "../../state/findings";
import { useTranslation } from "../../i18n/useTranslation";
import { getFriendlyRule } from "../../rules/ruleLogic";
import { liveController } from "../../live/liveController";
import { SuggestionCard } from "../common/SuggestionCard";
import { ApiRequestError, requestAiSuggestions } from "../../api/client";
import { resolvePlanAwareError } from "../../api/planErrors";
import { UpgradePlanButton } from "../common/UpgradePlanButton";
import type { AiSuggestionState, Repetition } from "../../state/types";
import { CollapsibleSection } from "../common/CollapsibleSection";
import { useFindingsAutoCollapse } from "../common/useFindingsAutoCollapse";

const useStyles = makeStyles({
  panel: { display: "flex", flexDirection: "column", gap: "12px" },
  configRow: { display: "flex", gap: "12px", flexWrap: "wrap" },
  repList: { display: "flex", flexDirection: "column", gap: "8px" },
  repCard: { display: "flex", flexDirection: "column", gap: "4px" },
  repHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  status: { color: tokens.colorNeutralForeground3 },
  statusError: { color: tokens.colorPaletteRedForeground1 },
  aiBlock: { display: "flex", flexDirection: "column", gap: "4px", marginTop: "4px" },
  aiSuggestions: { display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "2px" },
  aiChip: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "12px",
    background: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  quotaInfo: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, marginTop: "4px" },
  quotaExhausted: { color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200, marginTop: "4px" },
});

const escapeHtml = (value: string): string =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const highlightRepetitionSentence = (sentence: string, forms: string[]): string => {
  let result = escapeHtml(sentence);
  const sortedForms = [...new Set(forms)].sort((a, b) => b.length - a.length);

  sortedForms.forEach((form) => {
    const escaped = escapeHtml(form).replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
    const regex = new RegExp(
      "(^|[^a-zA-Z0-9\\u0370-\\u03ff\\u1f00-\\u1fff])(" +
        escaped +
        ")(?=[^a-zA-Z0-9\\u0370-\\u03ff\\u1f00-\\u1fff]|$)",
      "gi"
    );
    result = result.replace(regex, '$1<mark class="repetition-highlight">$2</mark>');
  });

  return result;
};

function severityLabel(severity: string, t: ReturnType<typeof useTranslation>["t"]): string {
  switch (severity.toUpperCase()) {
    case "HIGH":
      return t.severityHigh;
    case "MEDIUM":
      return t.severityMedium;
    case "LOW":
      return t.severityLow;
    default:
      return severity;
  }
}

function RepetitionCard({
  rep,
  aiState,
  onAskAi,
}: {
  rep: Repetition;
  aiState: AiSuggestionState;
  onAskAi: () => void;
}): JSX.Element {
  const styles = useStyles();
  const { t, format } = useTranslation();
  const severity = String(rep.severity || "LOW");
  const forms = Array.isArray(rep.forms) ? rep.forms.join(", ") : "";
  const context = rep.clusters?.[0]?.sentenceContext || "";
  const suggestions = Array.isArray(rep.replacementSuggestions) ? rep.replacementSuggestions : [];

  return (
    <Card className={styles.repCard}>
      <div className={styles.repHead}>
        <Text weight="semibold">{rep.displayWord || rep.stemGroup || ""}</Text>
        <Badge appearance="tint" size="small">
          {`${severityLabel(severity, t)} · ${format(t.liveRepOccurrences, {
            count: rep.totalCount || 0,
          })}`}
        </Badge>
      </div>
      {forms ? (
        <Text className={styles.muted}>
          {t.liveRepForms} {forms}
        </Text>
      ) : null}
      {context ? (
        <span
          className="sentence-context"
          dangerouslySetInnerHTML={{
            __html: highlightRepetitionSentence(context, rep.forms || []),
          }}
        />
      ) : null}
      {suggestions.length ? (
        <Text className={styles.muted}>
          {t.liveRepSuggestions} {suggestions.join(", ")}
        </Text>
      ) : null}

      {/* AI suggestions block */}
      <div className={styles.aiBlock}>
        <Button
          size="small"
          appearance="outline"
          disabled={aiState.loading}
          onClick={onAskAi}
        >
          {aiState.loading ? t.aiSuggestionsLoading : t.askAiForHelp}
        </Button>
        {aiState.error ? (
          <MessageBar intent="error">
            <MessageBarBody>
              {aiState.error}
              {aiState.error === t.quotaExceededMessage ||
              aiState.error === t.serviceForbiddenMessage ? (
                <UpgradePlanButton label={t.quotaExceededUpgrade} />
              ) : null}
            </MessageBarBody>
          </MessageBar>
        ) : null}
        {aiState.suggestions.length > 0 ? (
          <>
            <Text className={styles.muted}>{t.aiSuggestionsLabel}</Text>
            <div className={styles.aiSuggestions}>
              {aiState.suggestions.map((s, i) => (
                <span key={i} className={styles.aiChip}>{s}</span>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
}

const DEFAULT_AI_STATE: AiSuggestionState = { loading: false, suggestions: [], error: null };

export function LivePanel(): JSX.Element {
  const styles = useStyles();
  const { t, lang, format } = useTranslation();
  const rules = useStore((s) => s.rules);
  const catalogSections = useStore((s) => s.catalogSections);
  const liveEnabled = useStore((s) => s.liveEnabled);
  const liveStatus = useStore((s) => s.liveStatus);
  const liveSuggestions = useStore((s) => s.liveSuggestions);
  const currentIndex = useStore((s) => s.currentLiveSuggestionIndex);
  const liveParagraphText = useStore((s) => s.liveParagraphText);
  const repetitionEnabled = useStore((s) => s.liveRepetitionEnabled);
  const repetitionWindow = useStore((s) => s.liveRepetitionWindow);
  const repetitionFrequency = useStore((s) => s.liveRepetitionFrequency);
  const repetitions = useStore((s) => s.liveRepetitions);
  const liveRepAiSuggestions = useStore((s) => s.liveRepAiSuggestions);
  const liveRepRemainingQuota = useStore((s) => s.liveRepRemainingQuota);
  const patch = useStore((s) => s.patch);
  const token = useStore((s) => s.token);

  const hasCard = currentIndex >= 0 && currentIndex < liveSuggestions.length;
  const change = hasCard ? liveSuggestions[currentIndex] : null;
  const friendly = change ? getFriendlyRule(change.ruleId, rules, catalogSections, lang) : null;
  const hasFindings = useStore(hasLiveFindings);
  const { controlsExpanded, toggleControls, findingsRef } = useFindingsAutoCollapse(hasFindings);

  const onWindow = (_e: SyntheticEvent, data: SpinButtonOnChangeData) => {
    const value = data.value ?? Number(data.displayValue);
    if (Number.isFinite(value))
      liveController.setRepetitionConfig(Number(value), repetitionFrequency);
  };
  const onFrequency = (_e: SyntheticEvent, data: SpinButtonOnChangeData) => {
    const value = data.value ?? Number(data.displayValue);
    if (Number.isFinite(value)) liveController.setRepetitionConfig(repetitionWindow, Number(value));
  };

  /* Handle "Ask AI for help" for a specific repetition stem group */
  const handleAskAi = async (rep: Repetition) => {
    const key = rep.stemGroup || rep.displayWord || "";
    if (!key) return;

    patch({
      liveRepAiSuggestions: {
        ...liveRepAiSuggestions,
        [key]: { loading: true, suggestions: [], error: null },
      },
    });

    try {
      const context = rep.clusters?.[0]?.sentenceContext || "";
      const result = await requestAiSuggestions(
        token || undefined,
        rep.displayWord || rep.stemGroup || "",
        rep.forms || [],
        context,
      );
      patch({
        liveRepAiSuggestions: {
          ...liveRepAiSuggestions,
          [key]: { loading: false, suggestions: result?.suggestions ?? [], error: null },
        },
        liveRepRemainingQuota: result?.remainingQuota ?? liveRepRemainingQuota,
      });
    } catch (err) {
      /*
        Plan-limit failures use the shared localized upgrade state. Ordinary
        throttling retains its cooldown hint for retry guidance.
      */
      const planError = resolvePlanAwareError(err, {
        quota: t.quotaExceededMessage,
        forbidden: t.serviceForbiddenMessage,
        fallback: t.aiSuggestionsError,
      });
      const errorMessage = planError.showUpgrade
        ? planError.message
        : err instanceof ApiRequestError && err.status === 429 && err.retryAfterSeconds
          ? `${err.message} (try again in ~${err.retryAfterSeconds}s)`
          : err instanceof Error
            ? err.message
            : t.aiSuggestionsError;
      patch({
        liveRepAiSuggestions: {
          ...liveRepAiSuggestions,
          [key]: {
            loading: false,
            suggestions: [],
            error: errorMessage,
          },
        },
      });
    }
  };

  return (
    <div className="panel-section">
      <CollapsibleSection
        title={t.liveToggleLabel}
        expanded={controlsExpanded}
        onToggle={toggleControls}
      >
        <MessageBar intent="info" className="status-card">
          <MessageBarBody>{t.liveInfo}</MessageBarBody>
        </MessageBar>

        <Switch
          checked={liveEnabled}
          label={t.liveToggleLabel}
          onChange={(_e, data) => (data.checked ? liveController.start() : liveController.stop())}
        />
        <Text className={styles.muted}>{t.liveToggleHint}</Text>

        <Switch
          checked={repetitionEnabled}
          label={t.liveRepetitionLabel}
          onChange={(_e, data) => liveController.setRepetitionEnabled(Boolean(data.checked))}
        />
        <Text className={styles.muted}>{t.liveRepetitionHint}</Text>

        {repetitionEnabled ? (
          <div className={styles.configRow}>
            <Field label={t.liveRepWindowLabel} hint={t.liveRepWindowHint}>
              <SpinButton value={repetitionWindow} min={1} max={250} onChange={onWindow} />
            </Field>
            <Field label={t.liveRepFrequencyLabel} hint={t.liveRepFrequencyHint}>
              <SpinButton value={repetitionFrequency} min={2} max={20} onChange={onFrequency} />
            </Field>
          </div>
        ) : null}
      </CollapsibleSection>

      <div ref={findingsRef}>
        {/* Editorial suggestion card */}
        {change && friendly ? (
          <SuggestionCard
            counterText={format(t.liveCounter, {
              current: currentIndex + 1,
              total: liveSuggestions.length,
            })}
            progressPercent={((currentIndex + 1) / liveSuggestions.length) * 100}
            sentence={String(change.sentenceBefore || liveParagraphText || "")}
            before={String(change.before ?? "")}
            after={String(change.after ?? "")}
            ruleName={friendly.name}
            ruleTooltip={change.explanation || friendly.desc}
            onAccept={() => void liveController.accept()}
            onSkip={() => liveController.skip()}
          />
        ) : liveStatus.message ? (
          <Text className={liveStatus.variant === "error" ? styles.statusError : styles.status}>
            {liveStatus.message}
          </Text>
        ) : null}

        {/* Repetitions list */}
        {repetitionEnabled ? (
          <div>
            <Text weight="semibold">{t.liveRepetitionsHeader}</Text>
            {repetitions.length === 0 ? (
              <Text className={styles.muted}>{t.liveNoRepetitions}</Text>
            ) : (
              <div className={styles.repList}>
                {repetitions.map((rep, i) => {
                  const key = rep.stemGroup || rep.displayWord || String(i);
                  return (
                    <RepetitionCard
                      key={i}
                      rep={rep}
                      aiState={liveRepAiSuggestions[key] ?? DEFAULT_AI_STATE}
                      onAskAi={() => void handleAskAi(rep)}
                    />
                  );
                })}
              </div>
            )}
            {/* Quota status displayed below the list */}
            {liveRepRemainingQuota !== null ? (
              liveRepRemainingQuota > 0 ? (
                <Text className={styles.quotaInfo}>
                  {format(t.aiQuotaRemaining, { count: liveRepRemainingQuota })}
                </Text>
              ) : (
                <Text className={styles.quotaExhausted}>{t.aiQuotaExhausted}</Text>
              )
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
